import React, { useState, useMemo } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Grid, Card, CardContent,
  Stack, Chip, Avatar, Divider, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, List, ListItem, ListItemText
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ViewListIcon from '@mui/icons-material/ViewList';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import RoomIcon from '@mui/icons-material/Room';
import PersonIcon from '@mui/icons-material/Person';

const SubjectScheduleTab = ({ subject, schedules, subjectColor }) => {
  const [viewMode, setViewMode] = useState(0); // 0: Calendar, 1: List, 2: Table

  // Get day name in Arabic
  const getDayName = (dayValue) => {
    const days = {
      'sunday': 'الأحد',
      'monday': 'الاثنين',
      'tuesday': 'الثلاثاء',
      'wednesday': 'الأربعاء',
      'thursday': 'الخميس',
      'friday': 'الجمعة',
      'saturday': 'السبت'
    };
    return days[dayValue] || dayValue;
  };

  // Group schedules by day for calendar view
  const schedulesByDay = useMemo(() => {
    const grouped = {};
    const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    
    dayOrder.forEach(day => {
      grouped[day] = schedules?.filter(s => s.day_of_week === day) || [];
    });
    
    return grouped;
  }, [schedules]);

  // Render Weekly Calendar View
  const renderCalendarView = () => (
    <Grid container spacing={2}>
      {Object.entries(schedulesByDay).map(([day, daySchedules]) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={day}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 3,
              border: `2px solid ${daySchedules.length > 0 ? subjectColor : '#e0e0e0'}30`,
              minHeight: 200,
              height: '100%'
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color={daySchedules.length > 0 ? subjectColor : 'text.secondary'}>
                    {getDayName(day)}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${daySchedules.length} حصة`}
                    sx={{ bgcolor: `${subjectColor}20`, color: subjectColor, mt: 1 }}
                  />
                </Box>
                <Divider />
                {daySchedules.length > 0 ? (
                  <Stack spacing={1}>
                    {daySchedules
                      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                      .map((schedule, idx) => (
                        <Card
                          key={idx}
                          elevation={0}
                          sx={{
                            bgcolor: `${subjectColor}10`,
                            border: `1px solid ${subjectColor}30`,
                            borderRadius: 2,
                            p: 1.5
                          }}
                        >
                          <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight="bold">
                              {schedule.start_time} - {schedule.end_time}
                            </Typography>
                            {schedule.school_class_name && (
                              <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                                <SchoolIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {schedule.school_class_name}
                                </Typography>
                              </Stack>
                            )}
                            {schedule.classroom && (
                              <Stack useFlexGap direction="row" spacing={0.5} alignItems="center">
                                <RoomIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {schedule.classroom}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Card>
                      ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    لا توجد حصص
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  // Render List View
  const renderListView = () => (
    <List>
      {schedules
        ?.sort((a, b) => {
          const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
          const dayDiff = dayOrder.indexOf(a.day_of_week) - dayOrder.indexOf(b.day_of_week);
          if (dayDiff !== 0) return dayDiff;
          return (a.start_time || '').localeCompare(b.start_time || '');
        })
        .map((schedule, idx) => (
          <Card
            key={idx}
            elevation={1}
            sx={{
              mb: 2,
              borderRadius: 3,
              borderLeft: `4px solid ${subjectColor}`
            }}
          >
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: subjectColor, width: 40, height: 40 }}>
                      {getDayName(schedule.day_of_week).charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {getDayName(schedule.day_of_week)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {schedule.start_time} - {schedule.end_time}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {schedule.duration_minutes || 0} دقيقة
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={3}>
                  {schedule.school_class_name && (
                    <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                      <SchoolIcon fontSize="small" color="action" />
                      <Typography variant="body2">{schedule.school_class_name}</Typography>
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12} md={2}>
                  {schedule.classroom && (
                    <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                      <RoomIcon fontSize="small" color="action" />
                      <Typography variant="body2">{schedule.classroom}</Typography>
                    </Stack>
                  )}
                </Grid>
                <Grid item xs={12} md={2}>
                  {schedule.teacher_name && (
                    <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2">{schedule.teacher_name}</Typography>
                    </Stack>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      {(!schedules || schedules.length === 0) && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            لا توجد حصص مجدولة
          </Typography>
        </Paper>
      )}
    </List>
  );

  // Render Table View
  const renderTableView = () => (
    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: `${subjectColor}20` }}>
            <TableCell sx={{ fontWeight: 'bold' }}>اليوم</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>الوقت</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>المدة</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>الصف</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>القاعة</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>المعلم</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {schedules
            ?.sort((a, b) => {
              const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
              const dayDiff = dayOrder.indexOf(a.day_of_week) - dayOrder.indexOf(b.day_of_week);
              if (dayDiff !== 0) return dayDiff;
              return (a.start_time || '').localeCompare(b.start_time || '');
            })
            .map((schedule, idx) => (
              <TableRow key={idx} hover>
                <TableCell>
                  <Chip
                    size="small"
                    label={getDayName(schedule.day_of_week)}
                    sx={{ bgcolor: `${subjectColor}20`, color: subjectColor }}
                  />
                </TableCell>
                <TableCell>
                  {schedule.start_time} - {schedule.end_time}
                </TableCell>
                <TableCell>{schedule.duration_minutes || 0} دقيقة</TableCell>
                <TableCell>{schedule.school_class_name || '-'}</TableCell>
                <TableCell>{schedule.classroom || '-'}</TableCell>
                <TableCell>{schedule.teacher_name || '-'}</TableCell>
              </TableRow>
            ))}
          {(!schedules || schedules.length === 0) && (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  لا توجد حصص مجدولة
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* View Mode Tabs */}
      <Paper elevation={1} sx={{ mb: 3, borderRadius: 3 }}>
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 60,
              fontSize: '1rem',
              fontWeight: 'bold',
              '&.Mui-selected': { color: subjectColor }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              backgroundColor: subjectColor
            }
          }}
        >
          <Tab icon={<CalendarTodayIcon />} label="عرض التقويم" iconPosition="start" />
          <Tab icon={<ViewListIcon />} label="عرض القائمة" iconPosition="start" />
          <Tab icon={<TableChartIcon />} label="عرض الجدول" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Content based on view mode */}
      {viewMode === 0 && renderCalendarView()}
      {viewMode === 1 && renderListView()}
      {viewMode === 2 && renderTableView()}
    </Box>
  );
};

export default SubjectScheduleTab;
